import { type HTMLAttributes, type ReactElement } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import type MaTab from '../models/MaTab'
import styles from '../assets/scss/MaTabs.module.scss'
import { BsXLg } from 'react-icons/bs'

interface MaTabsProps extends HTMLAttributes<HTMLDivElement> {
  tabs: MaTab[]
  activeTab: string
  onSelectTab?: (tabKey: string) => void
  onCloseTab?: (tabKey: string) => void
}

function MaTabs ({
  tabs,
  activeTab,
  onSelectTab,
  onCloseTab
}: Readonly<MaTabsProps>): ReactElement {
  const { t } = useTranslation()

  const handleSelect = (key: string): boolean => {
    const tabToSelect = tabs.find(tab => tab.key === key)

    if (tabToSelect?.elementIsComponent) {
      return false
    }
    activeTab = key
    return true
  }

  return (
    <div className={styles['ma-tabs']}>
      <Tabs
        className={styles.tabs}
        activeKey={activeTab}
        onSelect={eventKey => {
          if (handleSelect(eventKey ?? '')) {
            onSelectTab?.(eventKey as string)
          }
        }}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.key}
            eventKey={tab.key}
            title={
              <div>
                {!tab.elementIsComponent && (
                  <span className={classNames({ 'no-data': !tab.hasData })}>
                    {tab.title ?? t(`tabs.tab.title.${tab.key}`)}
                  </span>
                )}
                {!tab.elementIsComponent && tab.closable && (
                  <div
                    className={styles.close}
                    role="button"
                    aria-label={t('common.button.labels.close')}
                    onClick={event => {
                      event.stopPropagation()
                      onCloseTab?.(tab.key)
                    }}
                  >
                    <BsXLg size={12} />
                  </div>
                )}
                {tab.elementIsComponent && (
                  <div
                    role="presentation"
                    onClick={e => {
                      e.stopPropagation()
                    }}
                  >
                    {tab.component}
                  </div>
                )}
              </div>
            }
          >
            {!tab.elementIsComponent && tab.component}
          </Tab>
        ))}
      </Tabs>
    </div>
  )
}

export default MaTabs
